import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { addDoc, collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, setDoc, Timestamp, updateDoc, where } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { confirmarSistema } from "../Components/PopupSistema/PopupSistema";
import { auth, db, storage } from "../Config/firebase";
import { gerarSequencial, obterMaiorSequencial } from "../Utils/sequencial";
import "./CadastroPropostaComercial.css";

import PropostaPrint from "./RelatorioPropostaComercialPrint";
import {
  calcularTotais,
  dadosEmpresaInicial,
  formatarCodigo,
  formatarDataBR,
  estadoInicial,
  grupoInicialProposta,
  hojeISO,
  moeda,
  normalizarTexto,
  numerarItensProposta,
  obraEstaAtiva,
  propostaComDefaults,
  tipoServicoInicial,
  tiposServicosPadraoProposta,
} from "./propostaComercialModel";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";

function filtrarOpcoesPorNome(options, state) {
  const termos = normalizarTexto(state.inputValue)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!termos.length) return options;

  return options.filter((option) => {
    const nome = normalizarTexto(option?.nome);
    return termos.every((termo) => nome.includes(termo));
  });
}

function FormularioNovoTipoServico({ salvandoCadastro, executarSalvamento, onSalvar, onImportar }) {
  const [formulario, setFormulario] = useState(() => ({
    ...tipoServicoInicial,
    tipo: String(tipoServicoInicial.tipo || "").toUpperCase(),
    categoria: String(tipoServicoInicial.categoria || "").toUpperCase(),
  }));

  function atualizarCampo(campo, valor) {
    const valorFormatado = ["tipo", "categoria"].includes(campo)
      ? String(valor || "").toUpperCase()
      : valor;
    setFormulario((atual) => ({ ...atual, [campo]: valorFormatado }));
  }

  function salvar() {
    executarSalvamento(async () => {
      const salvo = await onSalvar(formulario);
      if (salvo) {
        setFormulario({
          ...tipoServicoInicial,
          tipo: String(tipoServicoInicial.tipo || "").toUpperCase(),
          categoria: String(tipoServicoInicial.categoria || "").toUpperCase(),
        });
      }
    });
  }

  return (
    <>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <TextField label="Serviço" value={formulario.nome} onChange={(event) => atualizarCampo("nome", event.target.value)} fullWidth />
        <TextField label="Norma de referência" value={formulario.norma} onChange={(event) => atualizarCampo("norma", event.target.value)} fullWidth />
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <TextField label="Tipo" value={formulario.tipo} onChange={(event) => atualizarCampo("tipo", event.target.value)} sx={{ width: { md: 180 } }} />
        <TextField label="Categoria" value={formulario.categoria} onChange={(event) => atualizarCampo("categoria", event.target.value)} fullWidth />
        <Button variant="contained" onClick={salvar} disabled={salvandoCadastro}>
          {salvandoCadastro ? "Salvando..." : "Adicionar"}
        </Button>
        <Button variant="outlined" onClick={onImportar}>
          Importar padrões
        </Button>
      </Stack>
    </>
  );
}

export default function CadastroPropostaComercial() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const history = useHistory();
  const [propostas, setPropostas] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [tiposServicos, setTiposServicos] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [filtroValidade, setFiltroValidade] = useState("todas");
  const [salvandoAprovacao, setSalvandoAprovacao] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openTiposDialog, setOpenTiposDialog] = useState(false);
  const [openDadosCadastrais, setOpenDadosCadastrais] = useState(false);
  const [dadosCadastrais, setDadosCadastrais] = useState(dadosEmpresaInicial);
  const [dadosCadastraisForm, setDadosCadastraisForm] = useState(dadosEmpresaInicial);
  const [assinaturaArquivo, setAssinaturaArquivo] = useState(null);
  const [assinaturaPreview, setAssinaturaPreview] = useState("");
  const [removerAssinatura, setRemoverAssinatura] = useState(false);
  const [carregandoDadosCadastrais, setCarregandoDadosCadastrais] = useState(true);
  const [salvandoDadosCadastrais, setSalvandoDadosCadastrais] = useState(false);
  const [propostaEditando, setPropostaEditando] = useState(null);
  const [form, setForm] = useState(estadoInicial);
  const [interessadoInput, setInteressadoInput] = useState("");
  const [propostaPrint, setPropostaPrint] = useState(null);
  const [propostaSelecionandoModelo, setPropostaSelecionandoModelo] = useState(null);
  const [autoPrint, setAutoPrint] = useState(false);
  const [larguraRolagemTipos, setLarguraRolagemTipos] = useState(900);
  const tabelaTiposRef = React.useRef(null);
  const barraTiposRef = React.useRef(null);
  const totais = useMemo(() => calcularTotais(form), [form]);
  const [tipoServicoEditando, setTipoServicoEditando] = useState(null);

  useEffect(() => {
    carregarDados();
    carregarDadosCadastrais();
  }, []);

  useEffect(() => {
    if (!openTiposDialog) return undefined;

    function atualizarLarguraRolagemTipos() {
      const tabela = tabelaTiposRef.current;
      const barra = barraTiposRef.current;
      if (!tabela || !barra) return;

      const diferencaLargura = Math.max(0, barra.clientWidth - tabela.clientWidth);
      setLarguraRolagemTipos(tabela.scrollWidth + diferencaLargura);
    }

    const quadro = window.requestAnimationFrame(atualizarLarguraRolagemTipos);
    window.addEventListener("resize", atualizarLarguraRolagemTipos);
    return () => {
      window.cancelAnimationFrame(quadro);
      window.removeEventListener("resize", atualizarLarguraRolagemTipos);
    };
  }, [openTiposDialog, tiposServicos.length, tipoServicoEditando?.id]);

  async function carregarDadosCadastrais() {
    const tenantId = String(localStorage.getItem("tenantId") || "").trim();
    if (!tenantId) {
      setDadosCadastrais(dadosEmpresaInicial);
      setCarregandoDadosCadastrais(false);
      return;
    }

    try {
      const snap = await getDoc(doc(db, "tenant_branding", tenantId));
      const dados = {
        ...dadosEmpresaInicial,
        ...(snap.exists() ? snap.data()?.dadosPropostaComercial || {} : {}),
      };
      setDadosCadastrais(dados);
    } catch (error) {
      console.error("Erro ao carregar dados cadastrais da proposta:", error);
      setDadosCadastrais(dadosEmpresaInicial);
    } finally {
      setCarregandoDadosCadastrais(false);
    }
  }

  function abrirDadosCadastrais() {
    setDadosCadastraisForm({ ...dadosCadastrais });
    setAssinaturaArquivo(null);
    setAssinaturaPreview(dadosCadastrais.assinaturaUrl || "");
    setRemoverAssinatura(false);
    setOpenDadosCadastrais(true);
  }

  function atualizarDadosCadastrais(campo, valor) {
    setDadosCadastraisForm((atual) => ({ ...atual, [campo]: valor }));
  }

  function selecionarAssinatura(arquivo) {
    if (!arquivo) return;
    if (!arquivo.type?.startsWith("image/")) {
      alert("Selecione um arquivo de imagem válido.");
      return;
    }
    if (arquivo.size > 5 * 1024 * 1024) {
      alert("A imagem da assinatura deve ter no máximo 5 MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => setAssinaturaPreview(event.target?.result || "");
    reader.readAsDataURL(arquivo);
    setAssinaturaArquivo(arquivo);
    setRemoverAssinatura(false);
  }

  function limparAssinatura() {
    setAssinaturaArquivo(null);
    setAssinaturaPreview("");
    setRemoverAssinatura(true);
  }

  async function salvarDadosCadastrais() {
    const tenantId = String(localStorage.getItem("tenantId") || "").trim();
    if (!tenantId) {
      alert("Não foi possível identificar a empresa do usuário.");
      return;
    }

    const camposTabela = ["razaoSocial", "cnpj", "endereco", "bairro", "cidade", "cep", "contato", "siteEmail"];
    const preenchido = camposTabela.some((campo) => String(dadosCadastraisForm?.[campo] || "").trim());

    setSalvandoDadosCadastrais(true);
    try {
      let assinaturaUrl = dadosCadastraisForm.assinaturaUrl || "";
      let assinaturaPath = dadosCadastraisForm.assinaturaPath || "";

      if (removerAssinatura && assinaturaPath) {
        await deleteObject(ref(storage, assinaturaPath)).catch(() => {});
        assinaturaUrl = "";
        assinaturaPath = "";
      }

      if (assinaturaArquivo && preenchido) {
        const extensao = assinaturaArquivo.type === "image/png"
          ? "png"
          : assinaturaArquivo.type === "image/webp" ? "webp" : "jpg";
        const caminhoAnterior = assinaturaPath;
        assinaturaPath = `tenants/${tenantId}/branding/assinatura-proposta.${extensao}`;
        const assinaturaRef = ref(storage, assinaturaPath);
        await uploadBytes(assinaturaRef, assinaturaArquivo, {
          contentType: assinaturaArquivo.type,
          customMetadata: { tenantId },
        });
        assinaturaUrl = await getDownloadURL(assinaturaRef);
        if (caminhoAnterior && caminhoAnterior !== assinaturaPath) {
          await deleteObject(ref(storage, caminhoAnterior)).catch(() => {});
        }
      }

      const dadosPropostaComercial = {
        ...dadosCadastraisForm,
        preenchido,
        assinaturaUrl: preenchido ? assinaturaUrl : "",
        assinaturaPath: preenchido ? assinaturaPath : "",
      };

      if (!preenchido && assinaturaPath) {
        await deleteObject(ref(storage, assinaturaPath)).catch(() => {});
        dadosPropostaComercial.assinaturaUrl = "";
        dadosPropostaComercial.assinaturaPath = "";
      }

      await setDoc(
        doc(db, "tenant_branding", tenantId),
        {
          tenantId,
          dadosPropostaComercial,
          atualizadoEm: serverTimestamp(),
          atualizadoPor: auth.currentUser?.uid || null,
        },
        { merge: true }
      );

      setDadosCadastrais(dadosPropostaComercial);
      setOpenDadosCadastrais(false);
    } catch (error) {
      console.error("Erro ao salvar dados cadastrais da proposta:", error);
      alert("Não foi possível salvar os dados cadastrais.");
    } finally {
      setSalvandoDadosCadastrais(false);
    }
  }

  async function carregarDados() {
    const [clientesSnap, propostasSnap, tiposSnap] = await Promise.all([
      getDocs(consultaTenantAtual(query(collection(db, "clientes"), orderBy("nome")))),
      getDocs(consultaTenantAtual(query(collection(db, "tblPropostasComerciais"), orderBy("criadoEm", "desc")))),
      getDocs(consultaTenantAtual(query(collection(db, "tblTiposServicosProposta"), orderBy("nome")))),
    ]);
    setClientes(clientesSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setPropostas(propostasSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setTiposServicos(tiposSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setCarregandoLista(false);
  }

  async function carregarObras(clienteId) {
    if (!clienteId) {
      setObras([]);
      return;
    }
    const obrasSnap = await getDocs(consultaTenantAtual(query(collection(db, "obras"), where("clienteId", "==", clienteId), orderBy("nome"))));
    setObras(obrasSnap.docs.map((item) => ({ id: item.id, ...item.data() })).filter(obraEstaAtiva));
  }

  const clienteSelecionado = clientes.find((cliente) => cliente.id === form.interessadoId) || null;
  const obraSelecionada = obras.find((obra) => obra.id === form.empreendimentoId) || null;

  function propostaVencida(proposta) {
    if (proposta?.aprovada === true || !proposta?.validadeProposta) return false;
    return String(proposta.validadeProposta).slice(0, 10) < new Date().toISOString().slice(0, 10);
  }

  async function alterarAprovacao(proposta, aprovada) {
    if (salvandoAprovacao) return;
    setSalvandoAprovacao(proposta.id);
    try {
      const usuarioAtual = auth.currentUser;
      const dados = {
        aprovada,
        atualizadoEm: Timestamp.now(),
        atualizadoPor: usuarioAtual?.uid || null,
        atualizadoPorEmail: usuarioAtual?.email || "",
        atualizadoPorNome: usuarioAtual?.displayName || usuarioAtual?.email || "",
      };
      await updateDoc(doc(db, "tblPropostasComerciais", proposta.id), dados);
      setPropostas((atuais) => atuais.map((item) =>
        item.id === proposta.id ? { ...item, ...dados } : item
      ));
    } catch (error) {
      console.error("Erro ao salvar aprovação da proposta:", error);
      alert("Não foi possível salvar a aprovação da proposta.");
    } finally {
      setSalvandoAprovacao(null);
    }
  }

  const listaFiltrada = propostas.filter((proposta) => {
    const termo = normalizarTexto(filtro);
    if (filtroValidade === "vigentes" && propostaVencida(proposta)) return false;
    if (filtroValidade === "aprovadas" && proposta.aprovada !== true) return false;
    if (filtroValidade === "nao_aprovadas" && proposta.aprovada === true) return false;
    if (!termo) return true;
    return [proposta.codigo, proposta.interessado, proposta.empreendimento, proposta.destinatario].some((valor) =>
      normalizarTexto(valor).includes(termo)
    );
  });

  function atualizarForm(campo, valor) {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  }

  function novaProposta() {
    setPropostaEditando(null);
    setObras([]);
    setInteressadoInput("");
    setForm(propostaComDefaults({
      data: hojeISO(),
      validadeProposta: "",
      tabelas: [grupoInicialProposta()],
    }));
    setOpenDialog(true);
  }

  async function editarProposta(proposta) {
    try {
      await carregarObras(proposta.interessadoId);
      setPropostaEditando(proposta);
      setInteressadoInput(proposta.interessado || "");
      setForm(propostaComDefaults(proposta));
      setOpenDialog(true);
    } catch (error) {
      console.error("Erro ao abrir edição da proposta:", error);
      alert("Não foi possível carregar os dados para editar a proposta.");
    }
  }

  async function clonarProposta(proposta) {
    const dadosClonados = { ...propostaComDefaults(proposta) };
    delete dadosClonados.id;
    delete dadosClonados.criadoEm;
    delete dadosClonados.atualizadoEm;
    delete dadosClonados.totalProposta;

    setPropostaEditando(null);
    await carregarObras(dadosClonados.interessadoId);
    setInteressadoInput(dadosClonados.interessado || "");
    setForm({
      ...dadosClonados,
      aprovada: false,
      codigo: "",
      revisao: "00",
      data: hojeISO(),
    });
    setOpenDialog(true);
  }

  async function salvarProposta(abrir = false) {
    if (!form.interessadoId) {
      alert("Selecione o interessado cadastrado no sistema.");
      return;
    }

    if (!form.empreendimentoId) {
      alert("Selecione o empreendimento cadastrado no sistema.");
      return;
    }

    const codigo = propostaEditando?.id
      ? form.codigo
      : form.codigo || (await gerarSequencial({
          chave: "proposta_comercial",
          escopo: new Date(form.data || new Date()).getFullYear(),
          formato: {
            padding: 4,
            ano: new Date(form.data || new Date()).getFullYear(),
            template: "PC - {numero}/{ano}",
          },
          valorMinimo: obterMaiorSequencial(propostas, /(\d{1,})\/?\d{0,4}$/),
        })).codigo;

    const usuarioAtual = auth.currentUser;
    const dados = {
      ...numerarItensProposta(form),
      codigo,
      interessado: clienteSelecionado?.nome || "",
      empreendimento: obraSelecionada?.nome || "",
      revisao: "00",
      rq: "RQ 6.4",
      descontoPercentual: form.descontoPercentual || "",
      totalProposta: totais.total,
      atualizadoEm: Timestamp.now(),
      atualizadoPor: usuarioAtual?.uid || null,
      atualizadoPorEmail: usuarioAtual?.email || "",
      atualizadoPorNome: usuarioAtual?.displayName || usuarioAtual?.email || "",
    };

    let salvo;
    if (propostaEditando?.id) {
      await updateDoc(doc(db, "tblPropostasComerciais", propostaEditando.id), dados);
      salvo = { ...propostaEditando, ...dados };
    } else {
      const ref = await addDoc(collection(db, "tblPropostasComerciais"), aplicarTenantAtual({
        ...dados,
        criadoEm: Timestamp.now(),
        criadoPor: usuarioAtual?.uid || null,
        criadoPorEmail: usuarioAtual?.email || "",
        criadoPorNome: usuarioAtual?.displayName || usuarioAtual?.email || "",
      }));
      salvo = { id: ref.id, ...dados };
    }

    setOpenDialog(false);
    setPropostaEditando(null);
    await carregarDados();
    if (abrir) {
      history.push(`/app/proposta-comercial/${salvo.id}`);
    }
  }

  async function salvarTipoServico(dadosTipoServico) {
    if (!dadosTipoServico.nome.trim()) {
      alert("Informe o nome do serviço.");
      return false;
    }

    await addDoc(collection(db, "tblTiposServicosProposta"), aplicarTenantAtual({
      ...dadosTipoServico,
      nome: dadosTipoServico.nome.trim(),
      norma: dadosTipoServico.norma.trim(),
      tipo: String(dadosTipoServico.tipo || "").trim().toUpperCase(),
      categoria: String(dadosTipoServico.categoria || "").trim().toUpperCase(),
      criadoEm: Timestamp.now(),
    }));
    await carregarDados();
    return true;
  }

 async function importarServicosPadrao() {
  if (!(await confirmarSistema("Deseja importar todos os serviços padrão para o banco de dados?"))) {
    return;
  }

  for (const servico of tiposServicosPadraoProposta) {
    await addDoc(collection(db, "tblTiposServicosProposta"), aplicarTenantAtual({
      ...servico,
      nome: String(servico.nome || "").trim(),
      norma: String(servico.norma || "").trim(),
      tipo: String(servico.tipo || "Ensaio").trim().toUpperCase(),
      categoria: String(servico.categoria || "").trim().toUpperCase(),
      criadoEm: Timestamp.now(),
    }));
  }

  await carregarDados();
  alert("Serviços padrão importados com sucesso.");
}

  function iniciarEdicaoTipoServico(tipo) {
  if (!tipo.id) {
    alert("Este é um serviço padrão do sistema. Para alterar, edite diretamente no código.");
    return;
  }

  setTipoServicoEditando({
    id: tipo.id,
    nome: tipo.nome || "",
    norma: tipo.norma || "",
    tipo: String(tipo.tipo || "Ensaio").toUpperCase(),
    categoria: String(tipo.categoria || "").toUpperCase(),
  });
}

function cancelarEdicaoTipoServico() {
  setTipoServicoEditando(null);
}

async function salvarEdicaoTipoServico() {
  if (!tipoServicoEditando?.id) return;

  if (!String(tipoServicoEditando.nome || "").trim()) {
    alert("Informe o nome do serviço.");
    return;
  }

  await updateDoc(doc(db, "tblTiposServicosProposta", tipoServicoEditando.id), {
    nome: String(tipoServicoEditando.nome || "").trim(),
    norma: String(tipoServicoEditando.norma || "").trim(),
    tipo: String(tipoServicoEditando.tipo || "").trim().toUpperCase(),
    categoria: String(tipoServicoEditando.categoria || "").trim().toUpperCase(),
    atualizadoEm: Timestamp.now(),
  });

  setTipoServicoEditando(null);
  await carregarDados();
}

  if (propostaPrint) {
    return (
      <PropostaPrint
        proposta={propostaComDefaults(propostaPrint)}
        autoPrint={autoPrint}
        onVoltar={() => {
          setAutoPrint(false);
          setPropostaPrint(null);
        }}
      />
    );
  }

  return (
    <>
      <Navbar />

      <Box className="proposta-page">
       <Paper className="cabecalho-lista-cadastro" elevation={0}>
       <Box className="proposta-topo">
  <Box>
    <Typography variant="h5" fontWeight={800}>RQ 6.4 Proposta comercial</Typography>
    <Typography color="text.secondary">
      Cadastro e impressão de propostas para controle tecnológico
    </Typography>
  </Box>
</Box>

<Box className="proposta-acoes-topo">
  <Button variant="outlined" onClick={() => setOpenTiposDialog(true)}>
    Tipos de serviço
  </Button>

  <Button variant="outlined" onClick={abrirDadosCadastrais} disabled={carregandoDadosCadastrais}>
    {carregandoDadosCadastrais ? "Carregando dados..." : "Dados cadastrais"}
  </Button>

  <Button variant="contained" startIcon={<AddIcon />} onClick={novaProposta}>
    Nova proposta
  </Button>
</Box>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
          <TextField
            size="small"
            label="Pesquisar por código, cliente ou obra"
            value={filtro}
            onChange={(event) => setFiltro(event.target.value)}
            fullWidth
          />
          <TextField
  select
  size="small"
  label="Situação"
  value={filtroValidade}
  onChange={(event) => setFiltroValidade(event.target.value)}
  sx={{ width: { md: 170 } }}
>
  <MenuItem value="todas">Todas</MenuItem>
  <MenuItem value="vigentes">Válidas</MenuItem>
  <MenuItem value="aprovadas">Aprovadas</MenuItem>
  <MenuItem value="nao_aprovadas">Não aprovadas</MenuItem>
</TextField>
          <TextField size="small" label="Qtd" value={listaFiltrada.length} InputProps={{ readOnly: true }} sx={{ width: { md: 110 } }} />
        </Stack>
        </Paper>

        <Paper className="proposta-lista">
          <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Interessado</TableCell>
                <TableCell>Empreendimento</TableCell>
                <TableCell>Valida até</TableCell>
                <TableCell>Aprovação</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listaFiltrada.map((item) => (
                <TableRow key={item.id} hover sx={{ cursor: "pointer" }} onClick={() => history.push(`/app/proposta-comercial/${item.id}`)}>
                  <TableCell>{formatarCodigo(item.codigo)}</TableCell>
                  <TableCell className="lista-dado-secundario">{formatarDataBR(item.data)}</TableCell>
                  <TableCell className="lista-dado-principal">{item.interessado || "-"}</TableCell>
                  <TableCell className="lista-dado-principal">{item.empreendimento || "-"}</TableCell>
                  <TableCell className="lista-dado-secundario">
                    <Stack spacing={0.2}>
                      <span>{formatarDataBR(item.validadeProposta)}</span>
                      {propostaVencida(item) && (
                        <Typography variant="caption" color="error" fontWeight={800}>
                          Proposta vencida
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <TextField
                      select
                      size="small"
                      value={item.aprovada === true ? "aprovado" : "nao_aprovado"}
                      disabled={salvandoAprovacao !== null}
                      onChange={(event) => alterarAprovacao(item, event.target.value === "aprovado")}
                      SelectProps={{ inputProps: { "aria-label": `Aprovação da proposta ${item.codigo}` } }}
                      sx={{ minWidth: 150, "& .MuiSelect-select": { color: item.aprovada === true ? "success.main" : "text.primary" } }}
                    >
                      <MenuItem value="nao_aprovado">Não aprovado</MenuItem>
                      <MenuItem value="aprovado">Aprovado</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell className="lista-dado-destaque" align="right">{moeda(Array.isArray(item.tabelas) ? calcularTotais(item).total : item.totalProposta ?? calcularTotais(item).total)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Visualizar">
                      <IconButton size="small" onClick={(event) => { event.stopPropagation(); setPropostaSelecionandoModelo(item); }}>
                        <VisibilityIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={(event) => { event.stopPropagation(); editarProposta(item); }}>
                        <EditIcon sx={{ color: "#0288d1" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Clonar proposta">
                      <IconButton size="small" onClick={(event) => { event.stopPropagation(); clonarProposta(item); }}>
                        <ContentCopyIcon sx={{ color: "#0288d1" }} />
                      </IconButton>
                    </Tooltip>
                    <HistoricoCadastro colecao="tblPropostasComerciais" documentoId={item.id} registro={item} />
                  </TableCell>
                </TableRow>
              ))}
              {carregandoLista && <TableRow><TableCell colSpan={8}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
              {!carregandoLista && listaFiltrada.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">Nenhuma proposta cadastrada.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog
        open={Boolean(propostaSelecionandoModelo)}
        onClose={() => setPropostaSelecionandoModelo(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Visualizar proposta comercial</DialogTitle>
        <DialogContent dividers>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Selecione o modelo de relatório que deseja abrir.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => {
                setAutoPrint(false);
                setPropostaPrint({ ...propostaSelecionandoModelo, modeloRelatorio: "modelo1" });
                setPropostaSelecionandoModelo(null);
              }}
            >
              Modelo 1
            </Button>
            <Button
              fullWidth
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => {
                setAutoPrint(false);
                setPropostaPrint({ ...propostaSelecionandoModelo, modeloRelatorio: "modelo2" });
                setPropostaSelecionandoModelo(null);
              }}
            >
              Modelo 2
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPropostaSelecionandoModelo(null)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDadosCadastrais}
        onClose={() => !salvandoDadosCadastrais && setOpenDadosCadastrais(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Dados cadastrais das propostas</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Razão Social"
              value={dadosCadastraisForm.razaoSocial || ""}
              onChange={(event) => atualizarDadosCadastrais("razaoSocial", event.target.value)}
              fullWidth
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="CNPJ"
                value={dadosCadastraisForm.cnpj || ""}
                onChange={(event) => atualizarDadosCadastrais("cnpj", event.target.value)}
                fullWidth
              />
              <TextField
                label="CEP"
                value={dadosCadastraisForm.cep || ""}
                onChange={(event) => atualizarDadosCadastrais("cep", event.target.value)}
                fullWidth
              />
            </Stack>
            <TextField
              label="Endereço"
              value={dadosCadastraisForm.endereco || ""}
              onChange={(event) => atualizarDadosCadastrais("endereco", event.target.value)}
              fullWidth
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Bairro"
                value={dadosCadastraisForm.bairro || ""}
                onChange={(event) => atualizarDadosCadastrais("bairro", event.target.value)}
                fullWidth
              />
              <TextField
                label="Cidade"
                value={dadosCadastraisForm.cidade || ""}
                onChange={(event) => atualizarDadosCadastrais("cidade", event.target.value)}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Contato"
                value={dadosCadastraisForm.contato || ""}
                onChange={(event) => atualizarDadosCadastrais("contato", event.target.value)}
                fullWidth
              />
              <TextField
                label="Site/E-mail"
                value={dadosCadastraisForm.siteEmail || ""}
                onChange={(event) => atualizarDadosCadastrais("siteEmail", event.target.value)}
                fullWidth
              />
            </Stack>

            <Typography fontWeight={800}>Assinatura</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Nome do responsável"
                value={dadosCadastraisForm.nomeResponsavel || ""}
                onChange={(event) => atualizarDadosCadastrais("nomeResponsavel", event.target.value)}
                fullWidth
              />
              <TextField
                label="Empresa abaixo da assinatura"
                value={dadosCadastraisForm.empresaAssinatura || ""}
                onChange={(event) => atualizarDadosCadastrais("empresaAssinatura", event.target.value)}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <Button component="label" variant="outlined">
                {assinaturaPreview ? "Substituir assinatura" : "Selecionar assinatura"}
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => {
                    selecionarAssinatura(event.target.files?.[0]);
                    event.target.value = "";
                  }}
                />
              </Button>
              {assinaturaPreview && (
                <Button color="error" variant="outlined" onClick={limparAssinatura}>
                  Remover assinatura
                </Button>
              )}
            </Stack>
            {assinaturaPreview && (
              <Box
                component="img"
                src={assinaturaPreview}
                alt="Pré-visualização da assinatura"
                sx={{ width: 240, height: 90, objectFit: "contain", border: "1px solid #ddd", p: 1 }}
              />
            )}
            <Typography color="text.secondary" fontSize={13}>
              Estes dados serão usados em todas as propostas desta empresa, inclusive nas propostas já salvas.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDadosCadastrais(false)} disabled={salvandoDadosCadastrais}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={salvarDadosCadastrais} disabled={salvandoDadosCadastrais}>
            {salvandoDadosCadastrais ? "Salvando..." : "Salvar dados"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={() => !salvandoCadastro && setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>{propostaEditando ? "Editar pré-cadastro da proposta" : "Nova proposta comercial"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Código" value={form.codigo} onChange={(event) => atualizarForm("codigo", event.target.value)} sx={{ width: { md: 190 } }} />
              <TextField label="Data" type="date" InputLabelProps={{ shrink: true }} value={form.data} onChange={(event) => atualizarForm("data", event.target.value)} sx={{ width: { md: 170 } }} />
              <TextField label="Validade da proposta" type="date" InputLabelProps={{ shrink: true }} value={form.validadeProposta || ""} onChange={(event) => atualizarForm("validadeProposta", event.target.value)} sx={{ width: { md: 210 } }} />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Autocomplete
                options={clientes}
                filterOptions={(options) =>
                  filtrarOpcoesPorNome(options, { inputValue: interessadoInput })
                }
                noOptionsText={<Button size="small" onMouseDown={() => history.push("/app/clientes?novo=1")}>+ Cadastrar cliente</Button>}
                getOptionLabel={(option) => option?.nome || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={clienteSelecionado}
                inputValue={interessadoInput}
                onInputChange={(event, novoValor) => setInteressadoInput(novoValor)}
                onChange={async (event, value) => {
                  setInteressadoInput(value?.nome || "");
                  setForm((atual) => ({
                    ...atual,
                    interessadoId: value?.id || "",
                    interessado: value?.nome || "",
                    empreendimentoId: "",
                    empreendimento: "",
                  }));
                  await carregarObras(value?.id);
                }}
                renderInput={(params) => <TextField {...params} label="Interessado" />}
                fullWidth
              />
              <Autocomplete
                options={obras}
                filterOptions={filtrarOpcoesPorNome}
                noOptionsText={<Button size="small" onMouseDown={() => history.push("/app/cadastroobras?novo=1")}>+ Cadastrar obra</Button>}
                getOptionLabel={(option) => option?.nome || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={obraSelecionada}
                onChange={(event, value) => {
                  setForm((atual) => ({
                    ...atual,
                    empreendimentoId: value?.id || "",
                    empreendimento: value?.nome || "",
                  }));
                }}
                renderInput={(params) => <TextField {...params} label="Empreendimento" />}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Destinatário" value={form.destinatario} onChange={(event) => atualizarForm("destinatario", event.target.value)} fullWidth />
              <TextField label="E-mail" value={form.email} onChange={(event) => atualizarForm("email", event.target.value)} fullWidth />
              <TextField label="Telefone" value={form.telefone} onChange={(event) => atualizarForm("telefone", event.target.value)} sx={{ width: { xs: "100%", md: 260 }, flexShrink: 0 }} />
            </Stack>

            <Typography color="text.secondary" fontSize={13}>
              Os grupos, itens, valores e textos da proposta serão preenchidos na tela de descrição após salvar e abrir.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={salvandoCadastro}>Cancelar</Button>
          <Button variant="outlined" onClick={() => executarSalvamento(() => salvarProposta(false))} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar"}
          </Button>
          <Button variant="contained" onClick={() => executarSalvamento(() => salvarProposta(true))} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar e abrir descrição"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openTiposDialog}
        onClose={() => !salvandoCadastro && setOpenTiposDialog(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { width: "calc(100% - 24px)", maxWidth: 1200 } }}
      >
        <DialogTitle>Tipos de serviço</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <FormularioNovoTipoServico
              salvandoCadastro={salvandoCadastro}
              executarSalvamento={executarSalvamento}
              onSalvar={salvarTipoServico}
              onImportar={importarServicosPadrao}
            />

            <Paper
              ref={tabelaTiposRef}
              className="proposta-lista"
              onScroll={(event) => {
                if (barraTiposRef.current) {
                  barraTiposRef.current.scrollLeft = event.currentTarget.scrollLeft;
                }
              }}
              sx={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}
            >
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
  <TableRow>
    <TableCell sx={{ minWidth: 420 }}>Serviço</TableCell>
    <TableCell sx={{ minWidth: 115 }}>Norma</TableCell>
    <TableCell sx={{ minWidth: 110 }}>Tipo</TableCell>
    <TableCell sx={{ minWidth: 140 }}>Categoria</TableCell>
    <TableCell align="center" sx={{ width: 90, minWidth: 90, maxWidth: 90, px: 0.5 }}>Ações</TableCell>
  </TableRow>
</TableHead>

<TableBody>
  {tiposServicos.map((tipo, index) => {
  const editando = tipoServicoEditando?.id && tipoServicoEditando.id === tipo.id;
  const servicoPadrao = false;

    return (
      <TableRow key={tipo.id || `padrao-${index}`}>
        <TableCell>
          {editando ? (
            <TextField
              size="small"
              value={tipoServicoEditando.nome}
              multiline
              minRows={3}
              onChange={(event) =>
                setTipoServicoEditando((atual) => ({
                  ...atual,
                  nome: event.target.value,
                }))
              }
              fullWidth
            />
          ) : (
            tipo.nome
          )}
        </TableCell>

        <TableCell>
          {editando ? (
            <TextField
              size="small"
              value={tipoServicoEditando.norma}
              multiline
              minRows={3}
              onChange={(event) =>
                setTipoServicoEditando((atual) => ({
                  ...atual,
                  norma: event.target.value,
                }))
              }
              fullWidth
            />
          ) : (
            tipo.norma || "-"
          )}
        </TableCell>

        <TableCell>
          {editando ? (
            <TextField
              size="small"
              value={tipoServicoEditando.tipo}
              onChange={(event) =>
                setTipoServicoEditando((atual) => ({
                  ...atual,
                  tipo: event.target.value.toUpperCase(),
                }))
              }
              fullWidth
            />
          ) : (
            String(tipo.tipo || "-").toUpperCase()
          )}
        </TableCell>

        <TableCell>
          {editando ? (
            <TextField
              size="small"
              value={tipoServicoEditando.categoria}
              multiline
              minRows={3}
              onChange={(event) =>
                setTipoServicoEditando((atual) => ({
                  ...atual,
                  categoria: event.target.value.toUpperCase(),
                }))
              }
              fullWidth
            />
          ) : (
            String(tipo.categoria || "-").toUpperCase()
          )}
        </TableCell>

        <TableCell align="center">
          {editando ? (
            <Stack spacing={1} alignItems="stretch">
              <Button size="small" variant="contained" onClick={() => executarSalvamento(salvarEdicaoTipoServico)} disabled={salvandoCadastro}>
                {salvandoCadastro ? "Salvando..." : "Salvar"}
              </Button>

              <Button size="small" variant="outlined" onClick={cancelarEdicaoTipoServico}>
                Cancelar
              </Button>
            </Stack>
          ) : (
            <Tooltip title={servicoPadrao ? "Serviço padrão do sistema" : "Editar serviço"}>
              <span>
                <IconButton
  size="small"
  onClick={() => iniciarEdicaoTipoServico(tipo)}
>
  <EditIcon sx={{ color: "#0288d1" }} />
</IconButton>
              </span>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    );
  })}
</TableBody>
              
              </Table>
            </Paper>
          </Stack>
        </DialogContent>
        <Box
          ref={barraTiposRef}
          onScroll={(event) => {
            if (tabelaTiposRef.current) {
              tabelaTiposRef.current.scrollLeft = event.currentTarget.scrollLeft;
            }
          }}
          aria-label="Rolagem horizontal dos tipos de serviço"
          sx={{
            height: 20,
            overflowX: "auto",
            overflowY: "hidden",
            flexShrink: 0,
            borderTop: "1px solid #94a3b8",
          }}
        >
          <Box sx={{ width: larguraRolagemTipos, height: 1 }} />
        </Box>
        <DialogActions>
          <Button onClick={() => setOpenTiposDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
